import { Button, Modal } from "@salesforce/design-system-react";
import { useState } from "react";

import { Alert } from "../components/Alert";

import * as axios from "axios";
import { authCompleteEvent } from "./VimeoAuthComplete";
import { useSearchParams } from "react-router-dom";

export function VimeoAuth() {
    const [searchParams] = useSearchParams();
    const [alertMsg, setAlertMsg] = useState("");
    const client = axios.default.create({
        timeout: 20 * 1000,
    });
    let oauthWindow: Window | null;
    let timeoutId: NodeJS.Timeout | undefined;
    let checkingAuth = false;

    function removeEventListeners() {
        window.document.removeEventListener(
            "visibilitychange",
            onVisibilityChange
        );
        window.removeEventListener("message", onMessage);
    }

    async function checkAuthCompletion() {
        if (checkingAuth) {
            return;
        }

        checkingAuth = true;
        try {
            await client.get("/api/vimeo/auth-validated");
            oauthWindow?.close();
            removeEventListeners();
            const redirectUrl = searchParams.get("redirectUrl");
            // Only allow redirect to relative paths to keep the user
            // within the app and not allow anyone to abuse the
            // query-param.
            if (redirectUrl?.startsWith("/")) {
                window.location.href = redirectUrl;
            } else {
                window.location.href = "/";
            }
            return;
        } catch (err: any) {
            checkingAuth = false;
            if (err.response.status !== 401) {
                setAlertMsg(
                    "An unexpected error occurred. Please refresh this page."
                );
                return;
            }
        }

        checkingAuth = false;
        timeoutId = setTimeout(checkAuthCompletion, 3000);
    }

    async function onVisibilityChange() {
        if (document.visibilityState === "visible") {
            clearTimeout(timeoutId);
            await checkAuthCompletion();
        }
    }

    async function onMessage(e: MessageEvent) {
        const currentLocation = `${window.location.protocol}//${window.location.hostname}`;
        if (e.origin !== currentLocation) {
            console.warn("Ignoring message due to incorrect origin");
            return;
        }

        if (e.data !== authCompleteEvent) {
            console.warn("Ignoring message with unknown data");
            return;
        }

        clearTimeout(timeoutId);
        await checkAuthCompletion();
    }

    function onSignInClicked() {
        oauthWindow = window.open("/oauth2/vimeo/authorize", "_blank");
        if (!oauthWindow) {
            setAlertMsg("Failed to open a new tab for Vimeo .");
            return;
        }

        window.onmessage = onMessage;
        window.document.addEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        timeoutId = setTimeout(checkAuthCompletion, 3000);
    }

    return (
        <Modal
            disableClose
            footer={[
                <Button
                    key="signInBtn"
                    onClick={onSignInClicked}
                    variant="brand"
                >
                    Sign in
                </Button>,
            ]}
            isOpen={true}
            size="small"
            heading={<span>Sign In</span>}
            prompt="offline"
        >
            <div className="slds-var-m-around_medium">
                {alertMsg !== "" && (
                    <Alert
                        alertMsg={alertMsg}
                        variant="slds-alert_error"
                        onClose={() => setAlertMsg("")}
                    />
                )}
                <div className="slds-align_absolute-center slds-text-heading_small">
                    <p>Please sign in to your Vimeo account</p>
                </div>
            </div>
        </Modal>
    );
}
